import React from "react";
import { useTranslation } from "react-i18next";
import {
  ERROR_TRANSLATION_KEYS,
  MODE_CONFIG,
  NOTIFICATION_LOCALES,
  orderedKnownCategories,
  supportedReturnedModes,
} from "./notificationPreferencesConfig";
import useNotificationPreferences from "./useNotificationPreferences";
import NotificationHealthSection from "./NotificationHealthSection";
import { isPushTestNotificationUiEnabled } from "../../../services/pushTestNotificationsApi";

function errorKey(code, operation) {
  if (code === "notification_preferences_unavailable") {
    return operation === "load"
      ? "notification_preferences_load_error"
      : "notification_preferences_save_unavailable";
  }
  return ERROR_TRANSLATION_KEYS[code] ||
    (operation === "load"
      ? "notification_preferences_load_error"
      : "notification_preferences_save_error");
}

export default function NotificationPreferencesSection({ accountKey }) {
  const { t } = useTranslation();
  const preferences = useNotificationPreferences({ accountKey });
  const {
    status,
    draft,
    metadata,
    loadError,
    saveError,
    savedSuccessfully,
    saving,
    detectedTimezone,
    isDirty,
    validationError,
    actions,
  } = preferences;

  if (status === "loading" || status === "idle") {
    return (
      <section
        className="rp-notification-preferences"
        aria-labelledby="rp-notification-preferences-title"
        aria-busy="true"
      >
        <h3 id="rp-notification-preferences-title">
          {t("notification_preferences_title")}
        </h3>
        <p className="rp-notification-preferences__loading" role="status">
          {t("notification_preferences_loading")}
        </p>
      </section>
    );
  }

  if (status === "error" || !draft || !metadata) {
    return (
      <section
        className="rp-notification-preferences"
        aria-labelledby="rp-notification-preferences-title"
      >
        <h3 id="rp-notification-preferences-title">
          {t("notification_preferences_title")}
        </h3>
        <p className="rp-notification-preferences__error" role="alert">
          {t(errorKey(loadError, "load"))}
        </p>
        <button
          type="button"
          className="rp-notification-preferences__secondary-button"
          onClick={actions.retry}
        >
          {t("notification_preferences_retry")}
        </button>
      </section>
    );
  }

  const categories = orderedKnownCategories(metadata.categories);
  const modes = supportedReturnedModes(metadata.availableModes);
  const quietHoursError =
    validationError === "notification_quiet_hours_invalid" ||
    saveError === "notification_quiet_hours_invalid";
  const timezoneError = saveError === "notification_timezone_invalid";

  return (
    <section
      className="rp-notification-preferences"
      aria-labelledby="rp-notification-preferences-title"
      aria-busy={saving}
    >
      <div className="rp-notification-preferences__intro">
        <h3 id="rp-notification-preferences-title">
          {t("notification_preferences_title")}
        </h3>
        <p>{t("notification_preferences_description")}</p>
      </div>

      {isPushTestNotificationUiEnabled() ? (
        <div className="rp-notification-preferences__group">
          <NotificationHealthSection
            pushEnabled={draft.pushEnabled}
            directMessageMode={draft.categoryModes.direct_messages}
          />
        </div>
      ) : null}

      <div className="rp-notification-preferences__group">
        <h4>{t("notification_push_status_title")}</h4>
        <div className="rp-notification-preferences__status-card">
          <span
            className={`rp-notification-preferences__status-dot rp-notification-preferences__status-dot--${
              draft.pushEnabled ? "stored-active" : "inactive"
            }`}
            aria-hidden="true"
          />
          <div>
            <strong>
              {t(
                draft.pushEnabled
                  ? "notification_push_status_stored_active"
                  : "notification_push_status_inactive"
              )}
            </strong>
            <p>{t("notification_push_status_future_setup")}</p>
          </div>
        </div>
      </div>

      <div className="rp-notification-preferences__group">
        <h4>{t("notification_categories_title")}</h4>
        <p className="rp-notification-preferences__group-description">
          {t("notification_categories_description")}
        </p>
        <div className="rp-notification-preferences__category-list">
          {categories.map((category) => {
            const selectId = `notification-category-${category.id}`;
            const descriptionId = `${selectId}-description`;
            const selectedMode = draft.categoryModes[category.id];
            return (
              <div
                className="rp-notification-preferences__category"
                key={category.id}
              >
                <div>
                  <label htmlFor={selectId}>{t(category.titleKey)}</label>
                  <p id={descriptionId}>{t(category.descriptionKey)}</p>
                </div>
                <div className="rp-notification-preferences__mode-control">
                  <select
                    id={selectId}
                    value={selectedMode}
                    aria-describedby={descriptionId}
                    disabled={saving}
                    onChange={(event) =>
                      actions.setCategoryMode(category.id, event.target.value)
                    }
                  >
                    {modes.map((mode) => (
                      <option key={mode} value={mode}>
                        {t(MODE_CONFIG[mode].labelKey)}
                      </option>
                    ))}
                  </select>
                  <small>{t(MODE_CONFIG[selectedMode].descriptionKey)}</small>
                </div>
              </div>
            );
          })}
        </div>
        <p className="rp-notification-preferences__policy-note">
          {t("notification_mode_policy_note")}
        </p>
      </div>

      <div className="rp-notification-preferences__group">
        <h4>{t("notification_previews_title")}</h4>
        <label className="rp-notification-preferences__check-row">
          <input
            type="checkbox"
            checked={draft.previewsEnabled}
            disabled={saving}
            aria-describedby="notification-previews-description"
            onChange={(event) =>
              actions.setPreviewsEnabled(event.target.checked)
            }
          />
          <span>{t("notification_previews_enabled")}</span>
        </label>
        <p
          id="notification-previews-description"
          className="rp-notification-preferences__group-description"
        >
          {t("notification_previews_description")}
        </p>
      </div>

      <div className="rp-notification-preferences__group">
        <h4>{t("notification_quiet_hours_title")}</h4>
        <label className="rp-notification-preferences__check-row">
          <input
            type="checkbox"
            checked={draft.quietHours.enabled}
            disabled={saving}
            aria-describedby="notification-quiet-hours-description"
            onChange={(event) =>
              actions.setQuietHoursField("enabled", event.target.checked)
            }
          />
          <span>{t("notification_quiet_hours_enabled")}</span>
        </label>
        <p
          id="notification-quiet-hours-description"
          className="rp-notification-preferences__group-description"
        >
          {t("notification_quiet_hours_description")}
        </p>
        <div className="rp-notification-preferences__time-grid">
          <label>
            <span>{t("notification_quiet_hours_start")}</span>
            <input
              type="time"
              value={draft.quietHours.start}
              disabled={!draft.quietHours.enabled || saving}
              aria-invalid={quietHoursError}
              aria-describedby={quietHoursError ? "quiet-hours-error" : undefined}
              onChange={(event) =>
                actions.setQuietHoursField("start", event.target.value)
              }
            />
          </label>
          <label>
            <span>{t("notification_quiet_hours_end")}</span>
            <input
              type="time"
              value={draft.quietHours.end}
              disabled={!draft.quietHours.enabled || saving}
              aria-invalid={quietHoursError}
              aria-describedby={quietHoursError ? "quiet-hours-error" : undefined}
              onChange={(event) =>
                actions.setQuietHoursField("end", event.target.value)
              }
            />
          </label>
        </div>
        {quietHoursError ? (
          <p
            id="quiet-hours-error"
            className="rp-notification-preferences__inline-error"
            role="alert"
          >
            {t("notification_quiet_hours_invalid")}
          </p>
        ) : null}

        <div className="rp-notification-preferences__timezone">
          <h5>{t("notification_timezone_title")}</h5>
          <p>
            {draft.quietHours.timezone
              ? t("notification_timezone_current", {
                  timezone: draft.quietHours.timezone,
                })
              : t("notification_timezone_missing")}
          </p>
          {detectedTimezone ? (
            <p className="rp-notification-preferences__timezone-detected">
              {t("notification_timezone_detected", {
                timezone: detectedTimezone,
              })}
            </p>
          ) : (
            <p className="rp-notification-preferences__group-description">
              {t("notification_timezone_detection_unavailable")}
            </p>
          )}
          <div className="rp-notification-preferences__button-row">
            {detectedTimezone &&
            detectedTimezone !== draft.quietHours.timezone ? (
              <button
                type="button"
                className="rp-notification-preferences__secondary-button"
                disabled={saving}
                onClick={actions.useDetectedTimezone}
              >
                {t(
                  draft.quietHours.timezone
                    ? "notification_timezone_update_device"
                    : "notification_timezone_use_device",
                  { timezone: detectedTimezone }
                )}
              </button>
            ) : null}
            {draft.quietHours.timezone ? (
              <button
                type="button"
                className="rp-notification-preferences__secondary-button"
                disabled={saving}
                onClick={actions.clearTimezone}
              >
                {t("notification_timezone_clear")}
              </button>
            ) : null}
          </div>
          {timezoneError ? (
            <p className="rp-notification-preferences__inline-error" role="alert">
              {t("notification_timezone_invalid")}
            </p>
          ) : null}
        </div>
      </div>

      <div className="rp-notification-preferences__group">
        <h4>{t("notification_language_title")}</h4>
        <p
          id="notification-language-description"
          className="rp-notification-preferences__group-description"
        >
          {t("notification_language_description")}
        </p>
        <select
          className="rp-notification-preferences__language-select"
          value={draft.locale}
          disabled={saving}
          aria-label={t("notification_language_title")}
          aria-describedby="notification-language-description"
          onChange={(event) => actions.setLocale(event.target.value)}
        >
          {NOTIFICATION_LOCALES.map((locale) => (
            <option key={locale} value={locale}>
              {t(`notification_language_name_${locale}`)}
            </option>
          ))}
        </select>
      </div>

      <div className="rp-notification-preferences__save-row">
        <div
          className="rp-notification-preferences__save-feedback"
          aria-live="polite"
          role="status"
        >
          {savedSuccessfully
            ? t("notification_preferences_saved")
            : saveError
            ? t(errorKey(saveError, "save"))
            : ""}
        </div>
        <button
          type="button"
          className="rp-notification-preferences__save-button"
          disabled={!isDirty || saving || Boolean(validationError)}
          onClick={actions.save}
        >
          {t(
            saving
              ? "notification_preferences_saving"
              : "notification_preferences_save"
          )}
        </button>
      </div>
    </section>
  );
}
